import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Sengaja dinamai `lazy`: ini React.lazy yang dibungkus coba-ulang + muat ulang
// bersih saat chunk halaman gagal dimuat (deploy baru / sinyal putus).
// Lihat utils/lazyRetry.js — semua lazy() di bawah otomatis ikut terlindungi.
import lazy from './utils/lazyRetry';
import { cekSaatPindahMenu } from './utils/autoUpdate';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import { SSEProvider } from './contexts/SSEContext';
import PWAShell from './components/PWAShell';
import MemuatKonten from './components/MemuatKonten';
import getHome from './utils/getHome';
import PortalPerluLoginSendiri from './components/PortalPerluLoginSendiri';
// LoginPage sengaja TIDAK lazy: ini layar pertama yang dilihat user yang belum
// masuk, jadi memecahnya jadi potongan terpisah malah menambah satu putaran
// jaringan sebelum apa pun tampil.
import LoginPage from './pages/LoginPage';

import Ikon from './components/Ikon';
import { DOMAIN_MEMBER } from './member/rute';

// ── Error Boundary — tampil saat crash/blank, kasih tombol logout ──
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error('App error:', err);
  }
  handleReset = () => {
    // Harus membersihkan sessionStorage juga: login tanpa "Ingat saya" menaruh
    // token di sana, jadi kalau hanya localStorage yang dihapus user masih
    // dianggap masuk — LoginPage langsung melempar balik ke halaman semula,
    // halaman itu crash lagi, dan layar ini muncul terus. `?logout=1` bikin
    // LoginPage benar-benar mengosongkan sesi, bukan meneruskan ke home.
    localStorage.removeItem('ft_token');
    localStorage.removeItem('ft_user');
    sessionStorage.removeItem('ft_session_token');
    sessionStorage.removeItem('ft_session_user');
    window.location.href = '/login?logout=1';
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex items-center justify-center min-h-screen p-6 bg-slate-100">
        <div className="w-full max-w-sm p-10 space-y-4 text-center bg-white shadow-xl rounded-3xl">
          <div className="text-5xl"><Ikon e="⚠" /></div>
          <h2 className="text-xl font-bold text-slate-800">Terjadi Kesalahan</h2>
          <p className="text-sm text-slate-500">Aplikasi mengalami error. Klik tombol di bawah untuk kembali ke halaman login.</p>
          <button onClick={this.handleReset} className="w-full py-3 font-bold text-white transition-all bg-brand-600 hover:bg-brand-700 rounded-xl">
            <Ikon e="🔄" className="mr-1" />Kembali ke Login
          </button>
        </div>
      </div>
    );
  }
}

// Layouts
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));

// Portal Karyawan (self-service, mobile-first)
const KaryawanLayout = lazy(() => import('./pages/karyawan/KaryawanLayout'));
const KaryawanHome = lazy(() => import('./pages/karyawan/KaryawanHome'));
const KaryawanAbsensi = lazy(() => import('./pages/karyawan/KaryawanAbsensi'));
const KaryawanHistory = lazy(() => import('./pages/karyawan/KaryawanHistory'));
const KaryawanCuti = lazy(() => import('./pages/karyawan/KaryawanCuti'));
const KaryawanPayslip = lazy(() => import('./pages/karyawan/KaryawanPayslip'));
const KaryawanDataDiri = lazy(() => import('./pages/karyawan/KaryawanDataDiri'));
const KaryawanReview360 = lazy(() => import('./pages/karyawan/KaryawanReview360'));
const KaryawanInfo = lazy(() => import('./pages/karyawan/KaryawanInfo'));
const KaryawanSP = lazy(() => import('./pages/karyawan/KaryawanSP'));
const KaryawanDenda = lazy(() => import('./pages/karyawan/KaryawanDenda'));
const KaryawanKomisi = lazy(() => import('./pages/karyawan/KaryawanKomisi'));
// Portal Trainer (Fast Track) — halaman trainer di dalam Portal Karyawan; /pt/* mengarah ke sini.
// Portal Member (/m) — sesi & API terpisah dari staf (member/api.js).
const MemberApp = lazy(() => import('./member/MemberApp'));
const TrainerJadwal = lazy(() => import('./pages/karyawan/trainer/TrainerJadwal'));
const TrainerKlien = lazy(() => import('./pages/karyawan/trainer/TrainerKlien'));
const TrainerKetersediaan = lazy(() => import('./pages/karyawan/trainer/TrainerKetersediaan'));
const KaryawanPayroll = lazy(() => import('./pages/karyawan/KaryawanPayroll'));
const KaryawanJadwalShift = lazy(() => import('./pages/karyawan/KaryawanJadwalShift'));
const KaryawanBantuan = lazy(() => import('./pages/karyawan/KaryawanBantuan'));

// Admin pages — existing
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBeranda = lazy(() => import('./pages/admin/AdminBeranda'));
const AdminLaporanPresentasi = lazy(() => import('./pages/admin/AdminLaporanPresentasi'));
const AdminOperasionalDashboard = lazy(() => import('./pages/admin/AdminOperasionalDashboard'));
const AdminOperasionalLaporan = lazy(() => import('./pages/admin/AdminOperasionalLaporan'));
const AdminBranches = lazy(() => import('./pages/admin/AdminBranches'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const HRKaryawan = lazy(() => import('./pages/admin/HRKaryawan'));
const HRAbsensi = lazy(() => import('./pages/admin/HRAbsensi'));
const HRLaporanKehadiran = lazy(() => import('./pages/admin/HRLaporanKehadiran'));
const HRLaporanKeterlambatan = lazy(() => import('./pages/admin/HRLaporanKeterlambatan'));
const HRLaporanSP = lazy(() => import('./pages/admin/HRLaporanSP'));
const HRLaporanKPI = lazy(() => import('./pages/admin/HRLaporanKPI'));
const AdminTransactions = lazy(() => import('./pages/admin/AdminTransactions'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminExpenses = lazy(() => import('./pages/admin/AdminExpenses'));
const AdminRekonsiliasi = lazy(() => import('./pages/admin/AdminRekonsiliasi'));
const AdminExpenseDetail = lazy(() => import('./pages/admin/AdminExpenseDetail'));
const AdminPackages = lazy(() => import('./pages/admin/AdminPackages'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCashTransfers = lazy(() => import('./pages/admin/AdminCashTransfers'));
const AdminPenerimaanLain = lazy(() => import('./pages/admin/AdminPenerimaanLain'));
const AdminCommissionPayment = lazy(() => import('./pages/admin/AdminCommissionPayment'));
const AdminBranchDetail = lazy(() => import('./pages/admin/AdminBranchDetail'));
const AdminAllBranches = lazy(() => import('./pages/admin/AdminAllBranches'));
const AdminDiscounts = lazy(() => import('./pages/admin/AdminDiscounts'));
const AdminSuppliers = lazy(() => import('./pages/admin/AdminSuppliers'));
const AdminStock = lazy(() => import('./pages/admin/AdminStock'));
const AdminTransaksiEkuitas = lazy(() => import('./pages/admin/AdminTransaksiEkuitas'));
const AdminStockAdjustment = lazy(() => import('./pages/admin/AdminStockAdjustment'));
const AdminCustomerCategories = lazy(() => import('./pages/admin/AdminCustomerCategories'));
const AdminSalesInvoice = lazy(() => import('./pages/admin/AdminSalesInvoice'));
const PenerimaanPenjualan = lazy(() => import('./pages/admin/PenerimaanPenjualan'));
const AdminPurchasePayment = lazy(() => import('./pages/admin/AdminPurchasePayment'));
const AdminPurchaseInvoice = lazy(() => import('./pages/admin/AdminPurchaseInvoice'));

// Admin pages — new
const AdminCOA = lazy(() => import('./pages/admin/AdminCOA'));
const AdminMDR = lazy(() => import('./pages/admin/AdminMDR'));
const AdminMetodeBayar = lazy(() => import('./pages/admin/AdminMetodeBayar'));
const AdminActivityLog = lazy(() => import('./pages/admin/AdminActivityLog'));
const AdminCompanySetting = lazy(() => import('./pages/admin/AdminCompanySetting'));
// AdminPenerimaan tidak di-import lagi — menunya dihapus, path lamanya
// redirect ke Ekuitas Saldo Awal. File komponennya sengaja dibiarkan dulu
// kalau-kalau perlu dirujuk; hapus bersama endpoint /admin/penerimaan.
const AdminTutupBuku = lazy(() => import('./pages/admin/AdminTutupBuku'));
const AdminSaldoAwal = lazy(() => import('./pages/admin/AdminSaldoAwal'));
const AdminPersetujuan = lazy(() => import('./pages/admin/AdminPersetujuan'));
const AdminWaTemplates = lazy(() => import('./pages/admin/AdminWaTemplates'));
const AdminRefundAccounts = lazy(() => import('./pages/admin/AdminRefundAccounts'));
// Fast Track — Gym
const GymPaket = lazy(() => import('./pages/admin/gym/GymPaket'));
const GymMasterSederhana = lazy(() => import('./pages/admin/gym/GymMasterSederhana'));
const GymMember = lazy(() => import('./pages/admin/gym/GymMember'));
const GymMembership = lazy(() => import('./pages/admin/gym/GymMembership'));
const GymCheckin = lazy(() => import('./pages/admin/gym/GymCheckin'));
const GymLaporanMember = lazy(() => import('./pages/admin/gym/GymLaporanMember'));
const GymLaporanPdd = lazy(() => import('./pages/admin/gym/GymLaporanPdd'));
const GymJadwal = lazy(() => import('./pages/admin/gym/GymJadwal'));
const GymKetersediaan = lazy(() => import('./pages/admin/gym/GymKetersediaan'));
const GymAturan = lazy(() => import('./pages/admin/gym/GymAturan'));
const GymSesiPt = lazy(() => import('./pages/admin/gym/GymSesiPt'));
const GymDashboard = lazy(() => import('./pages/admin/gym/GymDashboard'));
const RekapHarian = lazy(() => import('./pages/admin/gym/RekapHarian'));
const GymRekapKelas = lazy(() => import('./pages/admin/gym/GymRekapKelas'));
const GymSurvei = lazy(() => import('./pages/admin/gym/GymSurvei'));
const GymLead = lazy(() => import('./pages/admin/gym/GymLead'));
const GymReferral = lazy(() => import('./pages/admin/gym/GymReferral'));
const GymKorporat = lazy(() => import('./pages/admin/gym/GymKorporat'));
const GymLoker = lazy(() => import('./pages/admin/gym/GymLoker'));
const GymImpor = lazy(() => import('./pages/admin/gym/GymImpor'));
const GymLaporanPenjualan = lazy(() => import('./pages/admin/gym/GymLaporanPenjualan'));
const GymAnalitik = lazy(() => import('./pages/admin/gym/GymAnalitik'));
const GymLaporanLead = lazy(() => import('./pages/admin/gym/GymLaporanLead'));
const AdminBanner = lazy(() => import('./pages/admin/AdminBanner'));
const AdminAboutUs = lazy(() => import('./pages/admin/AdminAboutUs'));
const AdminPushBroadcast = lazy(() => import('./pages/admin/AdminPushBroadcast'));
const AdminMemberAkun = lazy(() => import('./pages/admin/AdminMemberAkun'));
const AdminRoleAccess = lazy(() => import('./pages/admin/AdminRoleAccess'));
const AdminAssetCategories = lazy(() => import('./pages/admin/AdminAssetCategories'));
const AdminFixedAssets = lazy(() => import('./pages/admin/AdminFixedAssets'));

// HR pages
const HRDashboard = lazy(() => import('./pages/admin/HRDashboard'));
const HRKeterlambatan = lazy(() => import('./pages/admin/HRKeterlambatan'));
const HRPengurangan = lazy(() => import('./pages/admin/HRPengurangan'));
const HRShift = lazy(() => import('./pages/admin/HRShift'));
const HRJadwalShift = lazy(() => import('./pages/admin/HRJadwalShift'));
const HRCuti = lazy(() => import('./pages/admin/HRCuti'));
const HRDataGaji = lazy(() => import('./pages/admin/HRDataGaji'));
const HRPayrollGaji = lazy(() => import('./pages/admin/HRPayrollGaji'));
const HRPayrollTHR = lazy(() => import('./pages/admin/HRPayrollTHR'));
const HRPayrollCuti = lazy(() => import('./pages/admin/HRPayrollCuti'));
const HRPersetujuan = lazy(() => import('./pages/admin/HRPersetujuan'));
const AdminCoaSettings = lazy(() => import('./pages/admin/AdminCoaSettings'));
const HRPphBpjs = lazy(() => import('./pages/admin/HRPphBpjs'));
const HRKebutuhanStaf = lazy(() => import('./pages/admin/HRKebutuhanStaf'));
const HRSuratPeringatanConfig = lazy(() => import('./pages/admin/HRSuratPeringatanConfig'));
const HRSuratPeringatan = lazy(() => import('./pages/admin/HRSuratPeringatan'));
const HRMutasiKaryawan = lazy(() => import('./pages/admin/HRMutasiKaryawan'));

// Performance pages (KPI + QSC + Review 360). Setup (config) di Preferensi HR,
// penilaian (KPI report & QSC penilaian) di Performance.
const KpiReports = lazy(() => import('./pages/admin/KpiReports'));
const KpiSetup = lazy(() => import('./pages/admin/KpiSetup'));
const QscSetup = lazy(() => import('./pages/admin/QscSetup'));
const Review360Kategori = lazy(() => import('./pages/admin/Review360Kategori'));
const QscPenilaian = lazy(() => import('./pages/admin/QscPenilaian'));
const Review360 = lazy(() => import('./pages/admin/Review360'));

// Halaman kosong untuk role tanpa interface (appType 'none', mis. kapster).
// Sengaja minimal — cuma kasih tahu user tidak punya akses + tombol keluar
// supaya tidak terjebak tanpa cara logout.
const NoAccessPage = () => {
  const { user, logout } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3 px-6 text-center bg-slate-50">
      <p className="text-4xl"><Ikon e="🚫" /></p>
      <p className="text-base font-bold text-slate-600">Tidak ada halaman untuk role ini</p>
      <p className="max-w-sm text-sm text-slate-400">Akun Anda terdaftar sebagai data saja dan tidak memiliki akses ke interface mana pun.</p>
      <button onClick={logout} className="mt-2 btn-secondary">
        Keluar
      </button>
    </div>
  );
};

// Akun kasir POS (appType 'pos') yang login dengan email POS-nya: layarnya
// sudah tidak ada. Diberi tahu jelas + tombol keluar, bukan halaman kosong.
const PosDitutupPage = () => {
  const { user, logout } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3 px-6 text-center bg-slate-50">
      <p className="text-4xl"><Ikon e="🛎️" /></p>
      <p className="text-base font-bold text-slate-600">Layar POS tidak dipakai lagi</p>
      <p className="max-w-sm text-sm text-slate-400">Penjualan dan check-in member sekarang lewat menu <b>Front Desk</b> di Admin Panel. Minta admin membuatkan akun dengan role Front Desk.</p>
      <button onClick={logout} className="mt-2 btn-secondary">
        Keluar
      </button>
    </div>
  );
};

const PrivateRoute = ({ children, roles, appTypes, any, portal, tanpaSesiPortal }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;

  // Sesi login "Portal Karyawan" (HP kasir) tidak membuka POS. Arah ini cuma
  // dijaga di sini; arah sebaliknya dijaga server (middleware/auth.js → sesiPortal).
  if (tanpaSesiPortal && user.sesiApp === 'karyawan') return <Navigate to={getHome(user)} replace />;
  // Sesi POS kasir (tablet cabang, dipakai bergantian) tidak membuka portal.
  if (portal && user.appType === 'pos' && user.sesiApp !== 'karyawan') return <PortalPerluLoginSendiri />;

  // `any` → cukup terautentikasi (mis. Portal Karyawan, self-service per-user)
  if (any) return children;

  // Cek berdasarkan appType dulu (untuk dynamic roles)
  if (appTypes && user.appType && appTypes.includes(user.appType)) return children;

  // Cek berdasarkan role name (untuk backward compat)
  if (roles && roles.includes(user.role)) return children;

  // Tidak ada akses → redirect ke home sesuai appType
  return <Navigate to={getHome(user)} replace />;
};

// Placeholder buat halaman belum dibuat
const Placeholder = ({ title }) => (
  <div className="py-20 text-center card text-slate-400">
    <p className="mb-3 text-4xl"><Ikon e="🚧" /></p>
    <p className="text-lg font-bold text-slate-600">{title}</p>
    <p className="mt-1 text-sm">Halaman ini sedang dalam pengembangan</p>
  </div>
);

function AppRoutes() {
  const { user } = useAuth();

  // Cek build baru tiap pindah menu (di-throttle 2 menit di autoUpdate.js).
  // Pindah menu = momen chunk halaman baru diminta ke server, jadi di sinilah
  // paling penting device sudah tahu ada deploy baru sebelum meminta chunk lama.
  const location = useLocation();
  React.useEffect(() => { cekSaatPindahMenu(); }, [location.pathname]);

  // Domain member = HANYA Portal Member, di akar domain (/masuk, /kelas …).
  // Rute staf tidak dipasang sama sekali di sini; nginx juga mengalihkannya.
  if (DOMAIN_MEMBER) {
    return (
      <Suspense fallback={<MemuatKonten />}>
        <Routes>
          <Route path="/*" element={<MemberApp />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    // Suspense membungkus SELURUH Routes: tiap halaman kini potongan terpisah
    // yang diunduh saat dibuka, bukan sekaligus di awal. Kasir yang cuma pakai
    // POS tidak lagi ikut mengunduh 103 halaman admin.
    <Suspense fallback={<MemuatKonten />}>
    <Routes>
      {/* /login selalu bisa diakses — LoginPage handle redirect jika sudah login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/m/*" element={<MemberApp />} />
      {/* Portal Trainer (spec A7: /pt/*) = Portal Karyawan dengan menu trainer. */}
      <Route path="/pt" element={<Navigate to="/karyawan/pt/jadwal" replace />} />
      <Route path="/pt/*" element={<Navigate to="/karyawan/pt/jadwal" replace />} />

      {/* Layar POS DIHAPUS (klien gym 2 Okt 2026: "ga perlu POS"). Jualan &
          check-in lewat Admin → Gym → Front Desk; API /api/pos/* tetap dipakai
          Front Desk. Bookmark lama /pos diarahkan ke halaman awal masing-masing. */}
      <Route path="/pos/*" element={<Navigate to="/" replace />} />
      <Route path="/pos-ditutup" element={<PosDitutupPage />} />

      {/* Portal Karyawan — self-service, akses untuk semua user terautentikasi */}
      <Route
        path="/karyawan/*"
        element={
          <PrivateRoute any portal>
            <KaryawanLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<KaryawanHome />} />
        <Route path="absensi" element={<KaryawanAbsensi />} />
        <Route path="riwayat" element={<KaryawanHistory />} />
        <Route path="cuti" element={<KaryawanCuti />} />
        <Route path="payslip" element={<KaryawanPayslip />} />
        <Route path="data-diri" element={<KaryawanDataDiri />} />
        <Route path="review-360" element={<KaryawanReview360 />} />
        <Route path="info" element={<KaryawanInfo />} />
        <Route path="jadwal-shift" element={<KaryawanJadwalShift />} />
        <Route path="surat-peringatan" element={<KaryawanSP />} />
        <Route path="denda" element={<KaryawanDenda />} />
        <Route path="komisi" element={<KaryawanKomisi />} />
        <Route path="pt/jadwal" element={<TrainerJadwal />} />
        <Route path="pt/klien" element={<TrainerKlien />} />
        <Route path="pt/ketersediaan" element={<TrainerKetersediaan />} />
        <Route path="payroll" element={<KaryawanPayroll />} />
        <Route path="bantuan" element={<KaryawanBantuan />} />
      </Route>

      <Route
        path="/admin/*"
        element={
          <PrivateRoute roles={['admin', 'investor']} appTypes={['admin']}>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        {/* Beranda (dashboard eksekutif utama) + Dashboard operasional */}
        <Route path="beranda" element={<AdminBeranda />} />
        <Route path="laporan-presentasi" element={<AdminLaporanPresentasi />} />

        {/* ── OPERATIONAL (per area) ── */}
        <Route path="operasional" element={<AdminOperasionalDashboard />} />
        <Route path="operasional/laporan" element={<AdminOperasionalLaporan />} />
        <Route index element={<AdminDashboard />} />

        {/* ── GYM (Fast Track) ── */}
        <Route path="gym" element={<Navigate to="/admin/gym/dashboard" replace />} />
        <Route path="gym/dashboard" element={<GymDashboard />} />
        {/* Layar Front Desk dihapus 24 Okt 2026 → Check-in; penjualan dari Data Member */}
        <Route path="gym/front-desk" element={<Navigate to="/admin/gym/checkin" replace />} />
        <Route path="gym/checkin" element={<GymCheckin />} />
        <Route path="gym/rekap-harian" element={<RekapHarian />} />
        <Route path="gym/laporan/rekap-kelas" element={<GymRekapKelas />} />
        <Route path="gym/member/survei" element={<GymSurvei />} />
        {/* Follow-up Perpanjangan dihapus 2 Nov 2026 (klien: sudah ada di Laporan Gym → Expiry Forecast). */}
        <Route path="gym/member/follow-up" element={<Navigate to="/admin/gym/laporan/analitik?tab=expiry" replace />} />
        <Route path="gym/master" element={<Navigate to="/admin/gym/master/paket-membership" replace />} />
        <Route path="gym/member" element={<Navigate to="/admin/gym/member/data" replace />} />
        <Route path="gym/master/paket-membership" element={<GymPaket jenis="membership" />} />
        <Route path="gym/master/freeze-transfer" element={<GymPaket jenis="layanan" />} />
        <Route path="gym/master/paket-pt" element={<GymPaket jenis="pt" />} />
        <Route path="gym/master/day-pass" element={<GymPaket jenis="kelas" />} />
        <Route path="gym/master/jenis-kelas" element={<GymMasterSederhana jenis="kelas" />} />
        <Route path="gym/master/ruangan" element={<GymMasterSederhana jenis="ruangan" />} />
        <Route path="gym/master/tier" element={<GymMasterSederhana jenis="tier" />} />
        <Route path="gym/master/jenis-latihan" element={<GymMasterSederhana jenis="latihan" />} />
        <Route path="gym/master/jadwal" element={<GymJadwal />} />
        <Route path="gym/master/ketersediaan" element={<GymKetersediaan />} />
        <Route path="gym/master/aturan" element={<GymAturan />} />
        <Route path="gym/master/paket-kelas" element={<GymPaket jenis="paketkelas" />} />
        <Route path="gym/master/paket-loker" element={<GymPaket jenis="loker" />} />
        <Route path="gym/master/loker" element={<GymLoker />} />
        <Route path="gym/master/korporat" element={<GymKorporat />} />
        <Route path="gym/master/impor" element={<GymImpor />} />
        <Route path="gym/member/leads" element={<GymLead />} />
        <Route path="gym/member/referral" element={<GymReferral />} />
        <Route path="gym/laporan/penjualan" element={<GymLaporanPenjualan />} />
        <Route path="gym/laporan/analitik" element={<GymAnalitik />} />
        <Route path="gym/laporan/leads" element={<GymLaporanLead />} />
        <Route path="gym/member/booking" element={<GymJadwal mode="booking" />} />
        <Route path="gym/member/sesi-pt" element={<GymSesiPt />} />
        <Route path="gym/member/data" element={<GymMember />} />
        <Route path="gym/member/membership" element={<GymMembership />} />
        <Route path="gym/member/checkin" element={<Navigate to="/admin/gym/checkin" replace />} />
        <Route path="gym/laporan" element={<Navigate to="/admin/gym/laporan/member" replace />} />
        <Route path="gym/laporan/member" element={<GymLaporanMember />} />
        <Route path="gym/laporan/pdd" element={<GymLaporanPdd />} />

        {/* ── PREFERENSI (section "Administrator") ── */}
        <Route path="preferensi" element={<Navigate to="preferensi/role" replace />} />

        {/* ── CABANG (section "Accounting" → Preferensi → Cabang) ──
            Path kanonik sekarang /admin/cabang. Path lama
            (/admin/preferensi/cabang & /admin/hr/preferensi/cabang) tetap hidup. */}
        <Route path="cabang" element={<AdminBranches />} />
        <Route path="cabang/all" element={<AdminAllBranches />} />
        <Route path="cabang/:id/detail" element={<AdminBranchDetail />} />
        <Route path="preferensi/cabang" element={<AdminBranches />} />
        {/* Role + Hak Akses = satu menu bertab. Tiga path lama diarahkan ke sini
            supaya tab/bookmark yang sudah terlanjur dibuka tidak mati. */}
        <Route path="preferensi/role" element={<AdminRoleAccess />} />
        <Route path="preferensi/hak-akses" element={<Navigate to="/admin/preferensi/role" replace />} />
        <Route path="preferensi/roles" element={<Navigate to="/admin/preferensi/role" replace />} />
        <Route path="preferensi/users" element={<AdminUsers />} />
        <Route path="preferensi/perusahaan" element={<AdminCompanySetting />} />
        <Route path="hr/karyawan" element={<Navigate to="/admin/hr/db/karyawan" replace />} />

        {/* ── HR ── */}
        <Route path="hr" element={<HRDashboard />} />
        <Route path="hr/preferensi" element={<Navigate to="/admin/hr/preferensi/keterlambatan" replace />} />
        {/* Cabang pindah ke Accounting → Preferensi (/admin/cabang). Tiga path lama
            dibiarkan hidup supaya tab/bookmark yang terlanjur dibuka tidak mati. */}
        <Route path="hr/preferensi/cabang" element={<AdminBranches />} />
        <Route path="hr/preferensi/cabang/all" element={<AdminAllBranches />} />
        <Route path="hr/preferensi/cabang/:id/detail" element={<AdminBranchDetail />} />
        <Route path="hr/preferensi/role" element={<Navigate to="/admin/preferensi/role" replace />} />
        <Route path="hr/preferensi/keterlambatan" element={<HRKeterlambatan />} />
        <Route path="hr/preferensi/pengurangan" element={<HRPengurangan />} />
        <Route path="hr/preferensi/shift" element={<HRShift />} />
        <Route path="hr/db/jadwal-shift" element={<HRJadwalShift />} />
        <Route path="hr/preferensi/cuti" element={<HRCuti />} />
        <Route path="hr/preferensi/pph-bpjs" element={<HRPphBpjs />} />
        <Route path="hr/preferensi/sp-config" element={<HRSuratPeringatanConfig />} />
        <Route path="hr/preferensi/kebutuhan-staf" element={<HRKebutuhanStaf />} />
        <Route path="hr/persetujuan" element={<HRPersetujuan />} />
        <Route path="hr/db" element={<Navigate to="/admin/hr/db/karyawan" replace />} />
        <Route path="hr/db/absensi" element={<HRAbsensi />} />
        <Route path="hr/laporan" element={<Navigate to="/admin/hr/laporan/kehadiran" replace />} />
        <Route path="hr/laporan/kehadiran" element={<HRLaporanKehadiran />} />
        <Route path="hr/laporan/keterlambatan" element={<HRLaporanKeterlambatan />} />
        <Route path="hr/laporan/surat-peringatan" element={<HRLaporanSP />} />
        <Route path="hr/laporan/pencapaian-kpi" element={<HRLaporanKPI />} />
        <Route path="hr/db/karyawan" element={<HRKaryawan />} />
        <Route path="hr/db/gaji" element={<HRDataGaji />} />
        <Route path="hr/db/surat-peringatan" element={<HRSuratPeringatan />} />
        <Route path="hr/db/mutasi" element={<HRMutasiKaryawan />} />
        {/* Path lama masih dipakai bookmark & tab yang terbuka. */}
        <Route path="hr/db/transfer" element={<Navigate to="/admin/hr/db/mutasi" replace />} />
        <Route path="hr/payroll" element={<Navigate to="/admin/hr/payroll/gaji" replace />} />
        <Route path="hr/payroll/gaji" element={<HRPayrollGaji />} />
        <Route path="hr/payroll/komisi" element={<AdminCommissionPayment />} />
        <Route path="hr/payroll/thr" element={<HRPayrollTHR />} />
        <Route path="hr/payroll/cuti" element={<HRPayrollCuti />} />

        {/* ── PERFORMANCE (KPI + QSC penilaian) ── */}
        <Route path="performance" element={<Navigate to="/admin/performance/kpi-reports" replace />} />
        <Route path="performance/kpi-reports" element={<KpiReports />} />
        <Route path="performance/qsc" element={<QscPenilaian />} />
        <Route path="performance/review-360" element={<Review360 />} />
        {/* Setup (config) — admin only, di Administrator → Preferensi */}
        <Route path="preferensi/kpi-setup" element={<KpiSetup />} />
        <Route path="preferensi/qsc-setup" element={<QscSetup />} />
        <Route path="preferensi/kategori-review" element={<Review360Kategori />} />

        {/* Redirect path lama supaya link/bookmark tidak mati */}
        <Route path="hr/preferensi/kpi-setup" element={<Navigate to="/admin/preferensi/kpi-setup" replace />} />
        <Route path="hr/preferensi/qsc-setup" element={<Navigate to="/admin/preferensi/qsc-setup" replace />} />
        <Route path="performance/kpi-setup" element={<Navigate to="/admin/preferensi/kpi-setup" replace />} />
        <Route path="performance/qsc-setup" element={<Navigate to="/admin/preferensi/qsc-setup" replace />} />
        <Route path="preferensi/mdr" element={<AdminMDR />} />
        <Route path="preferensi/metode-bayar" element={<AdminMetodeBayar />} />
        {/* "Kas Transfer POS" dipensiunkan — rekening bank tujuan setoran kas sekarang
            diatur di Pengaturan COA (KAS_BCA). Redirect supaya link lama tidak mati. */}
        <Route path="preferensi/kas-transfer-pos" element={<Navigate to="/admin/preferensi/pengaturan-coa" replace />} />
        <Route path="preferensi/coa" element={<AdminCOA />} />
        <Route path="preferensi/pengaturan-coa" element={<AdminCoaSettings />} />
        {/* Halaman COA HR lama (hr/preferensi/coa-setting) dipensiunkan — akun HR
            sekarang ikut diatur di Pengaturan COA. Redirect supaya bookmark/link
            lama tidak mati. */}
        <Route path="hr/preferensi/coa-setting" element={<Navigate to="/admin/preferensi/pengaturan-coa" replace />} />
        {/* Menu "Penerimaan" (penerimaan modal) dihapus: fungsinya sama dengan
            Ekuitas Saldo Awal, yang sudah menangani setoran modal & saldo awal
            sekaligus. Path lama diarahkan ke sana. */}
        <Route path="preferensi/penerimaan" element={<Navigate to="/admin/preferensi/saldo-awal" replace />} />
        <Route path="preferensi/saldo-awal" element={<AdminSaldoAwal />} />
        <Route path="persetujuan" element={<AdminPersetujuan />} />
        <Route path="penjualan/diskon" element={<AdminDiscounts />} />
        <Route path="preferensi/diskon" element={<Navigate to="/admin/penjualan/diskon" replace />} />
        <Route path="preferensi/log-aktivitas" element={<AdminActivityLog />} />

        {/* ── POS (Desain Struk & Template WA dipindah dari Preferensi + Refund) ── */}
        <Route path="pos" element={<Navigate to="/admin/pos/refund-akun" replace />} />
        <Route path="pos/template-wa" element={<AdminWaTemplates />} />
        <Route path="pos/refund-akun" element={<AdminRefundAccounts />} />
        {/* CRM — pengaturan aplikasi PELANGGAN (domain terpisah, lihat frontend-member/) */}
        <Route path="member/banner" element={<AdminBanner />} />
        <Route path="member/about-us" element={<AdminAboutUs />} />
        <Route path="member/notifikasi" element={<AdminPushBroadcast />} />
        <Route path="member/akun" element={<AdminMemberAkun />} />
        {/* Redirect path lama → POS */}
        <Route path="preferensi/template-wa" element={<Navigate to="/admin/pos/template-wa" replace />} />

        {/* ── KAS & BANK ── */}
        <Route path="kas-bank" element={<Navigate to="kas-bank/biaya" replace />} />
        <Route path="kas-bank/biaya/:id" element={<AdminExpenseDetail />} />
        <Route path="kas-bank/biaya" element={<AdminExpenses />} />
        <Route path="kas-bank/transfer" element={<AdminCashTransfers />} />
        <Route path="kas-bank/penerimaan" element={<AdminPenerimaanLain />} />
        <Route path="kas-bank/rekonsiliasi" element={<AdminRekonsiliasi />} />
        <Route path="kas-bank/ekuitas" element={<AdminTransaksiEkuitas />} />
        {/* Pembayaran Komisi pindah ke HR → Payroll → Bayar Komisi (path lama tetap jalan) */}
        <Route path="kas-bank/pembayaran-komisi" element={<Navigate to="/admin/hr/payroll/komisi" replace />} />

        {/* ── PERUSAHAAN ── */}
        <Route path="perusahaan" element={<Navigate to="perusahaan/tutup-buku" replace />} />
        <Route path="perusahaan/tutup-buku" element={<AdminTutupBuku />} />

        {/* ── PENJUALAN ── */}
        <Route path="penjualan" element={<Navigate to="penjualan/transaksi" replace />} />
        <Route path="penjualan/transaksi" element={<AdminTransactions />} />
        <Route path="penjualan/pelanggan" element={<AdminCustomers />} />
        <Route path="penjualan/kategori-pelanggan" element={<AdminCustomerCategories />} />
        <Route path="penjualan/faktur" element={<AdminSalesInvoice />} />
        {/* Legacy: menu Pesanan (rantai HO→cabang) sudah dihapus. */}
        <Route path="penjualan/pesanan" element={<Navigate to="/admin/penjualan/faktur" replace />} />
        <Route path="penjualan/penerimaan" element={<PenerimaanPenjualan />} />

        {/* ── PEMBELIAN ── */}
        <Route path="pembelian" element={<Navigate to="pembelian/faktur" replace />} />
        <Route path="pembelian/faktur" element={<AdminPurchaseInvoice />} />
        <Route path="pembelian/pemasok" element={<AdminSuppliers />} />
        <Route path="pembelian/pembayaran" element={<AdminPurchasePayment />} />
        {/* Legacy: dulu "Pembayaran HO" & "Pembayaran Cabang" (rantai HO→cabang, sudah dihapus). */}
        <Route path="pembelian/pembayaran-ho" element={<Navigate to="/admin/pembelian/pembayaran" replace />} />
        <Route path="pembelian/pembayaran-cabang" element={<Navigate to="/admin/penjualan/penerimaan" replace />} />

        {/* ── PERSEDIAAN ── */}
        <Route path="persediaan" element={<Navigate to="persediaan/paket" replace />} />
        <Route path="persediaan/paket" element={<AdminPackages />} />
        <Route path="persediaan/stok" element={<AdminStock />} />
        <Route path="persediaan/penyesuaian" element={<AdminStockAdjustment />} />
        <Route path="persediaan/kategori" element={<AdminPackages />} />
        {/* Menu Sub-Kategori dihapus — tab-nya tidak ada lagi di halaman Barang & Jasa.
            Path lama diarahkan ke Kategori supaya bookmark/tab tersimpan tidak mati. */}
        <Route path="persediaan/sub-kategori" element={<Navigate to="/admin/persediaan/kategori" replace />} />
        <Route path="persediaan/merek" element={<AdminPackages />} />

        {/* ── LAPORAN ── */}
        <Route path="laporan" element={<AdminReports />} />
        <Route path="laporan/penjualan" element={<AdminReports />} />
        <Route path="laporan/kas-bank" element={<AdminReports />} />
        <Route path="aset-tetap/kategori" element={<AdminAssetCategories />} />
        <Route path="aset-tetap/daftar" element={<AdminFixedAssets />} />

        {/* ── Legacy routes (backward compat) ── */}
        {/* PENTING: branches/all dan branches/:id/detail harus SEBELUM redirect branches */}
        <Route path="branches/all" element={<AdminAllBranches />} />
        <Route path="branches/:id/detail" element={<AdminBranchDetail />} />
        <Route path="branches" element={<Navigate to="/admin/preferensi/cabang" replace />} />
        <Route path="users" element={<Navigate to="/admin/preferensi/users" replace />} />
        <Route path="transactions" element={<Navigate to="/admin/penjualan/transaksi" replace />} />
        <Route path="packages" element={<Navigate to="/admin/persediaan/paket" replace />} />
        <Route path="customers" element={<Navigate to="/admin/penjualan/pelanggan" replace />} />
        <Route path="expenses" element={<Navigate to="/admin/kas-bank/biaya" replace />} />
        <Route path="reports" element={<Navigate to="/admin/laporan/penjualan" replace />} />
        <Route path="cash-transfers" element={<Navigate to="/admin/kas-bank/transfer" replace />} />

        {/* Branch detail — new paths */}
        <Route path="preferensi/cabang/all" element={<AdminAllBranches />} />
        <Route path="preferensi/cabang/:id/detail" element={<AdminBranchDetail />} />
      </Route>

      {/* Halaman kosong untuk role tanpa interface (appType 'none') */}
      <Route path="/no-access" element={<NoAccessPage />} />

      <Route path="/" element={user ? <Navigate to={getHome(user)} replace /> : <Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          {/* Satu koneksi SSE untuk seluruh app — WAJIB di dalam AuthProvider
              (butuh user) dan di luar route (biar tidak putus tiap pindah menu). */}
          <SSEProvider>
            <AlertProvider>
              <AppRoutes />
              <PWAShell />
            </AlertProvider>
          </SSEProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
